import { Component, EventEmitter, Inject, Output } from '@angular/core';
import {
	MatDialogRef,
	MAT_DIALOG_DATA,
	MatDialogContent,
	MatDialogActions,
} from '@angular/material/dialog';
import { BehaviorSubject, interval, Observable, of } from 'rxjs';
import { debounce, mergeMap, tap } from 'rxjs/operators';
import { Company, emptyCompany } from 'src/app/models/data/company';
import { Device } from 'src/app/models/data/device';
import { SystemCapabilityEnum } from 'src/app/models/data/systemCapability';
import { ApiService, QueryParam } from 'src/app/services/api.services';
import { SystemCapabilityService } from 'src/app/services/system-capabilities/system-capability.service';
import { MatDivider } from '@angular/material/list';
import { CdkScrollable } from '@angular/cdk/scrolling';
import {
	MatFormField,
	MatLabel,
	MatPrefix,
} from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import {
	MatAutocompleteTrigger,
	MatAutocomplete,
} from '@angular/material/autocomplete';
import { MatIcon } from '@angular/material/icon';
import { MatOption } from '@angular/material/select';
import { MatButton } from '@angular/material/button';
import { AsyncPipe } from '@angular/common';

@Component({
	selector: 'app-transfer-device-ownership-dialog',
	templateUrl: './transfer-device-ownership-dialog.component.html',
	styleUrls: [
		'./transfer-device-ownership-dialog.component.scss',
		'../../../common/main.scss',
	],
	imports: [
		MatDivider,
		CdkScrollable,
		MatDialogContent,
		MatFormField,
		MatLabel,
		MatInput,
		MatAutocompleteTrigger,
		MatIcon,
		MatPrefix,
		MatAutocomplete,
		MatOption,
		MatDialogActions,
		MatButton,
		AsyncPipe,
	],
})
export class TransferDeviceOwnershipDialogComponent {
	companySearchEvents: BehaviorSubject<string> = new BehaviorSubject<string>(
		''
	);
	inputChangeEvents: BehaviorSubject<Company> = new BehaviorSubject<Company>(
		emptyCompany()
	);
	companySearchResults: Observable<any>;

	serverCallInProgress = false;

	@Output() selectedCompanyEvents = new EventEmitter<Company>();

	company: Company = emptyCompany();
	originalCompany: Company;
	selectedDevices: Device[] = [];
	showTransferDeviceOwnershipViewDisabled = true;
	showTransferDeviceOwnershipConfirmation = false;
	showTransferDeviceOwnershipConfirmationSuccess = false;

	get nextButtonDisabled(): boolean {
		return this.company.id === '';
	}

	constructor(
		@Inject(MAT_DIALOG_DATA)
		public data: {
			devices: Device[];
			originalCompany: Company;
		},
		private readonly apiService: ApiService,
		private readonly dialogRef: MatDialogRef<TransferDeviceOwnershipDialogComponent>,
		private readonly scService: SystemCapabilityService
	) {
		this.selectedDevices = data.devices;
		this.originalCompany = data.originalCompany;
		this.companySearchResults = this.companySearchEvents.asObservable().pipe(
			tap((it) => {
				if (it.length === 0) {
					this.company = emptyCompany();
					this.emitCompany();
				}
			}),
			debounce(() => interval(300)),
			mergeMap((companyName) => {
				if (companyName.length < 2) {
					return of([]);
				}

				const noResultsText = $localize`:@@transferDeviceOwnershipDialog-noResults:No results found for '${companyName}' Please enter a different search criteria.`;

				const queryParameters: QueryParam[] = [
					{ param: 'companyName', value: companyName },
					{ param: 'rowIndex', value: 0 },
					{ param: 'rowCount', value: 100 },
				];
				if (this.scService.has(SystemCapabilityEnum.globalCompanyManagement)) {
					return this.apiService
						.searchCompaniesPublicDirectory(queryParameters)
						.then((company) => {
							if (company.length === 0) {
								return [noResultsText];
							}
							return company;
						});
				} else {
					return this.apiService
						.getCompanyRelationships(queryParameters)
						.then((companyEntityRelationship) => {
							if (companyEntityRelationship.length === 0) {
								return [noResultsText];
							}
							return companyEntityRelationship.map(
								(company) => company.company
							);
						});
				}
			})
		);
	}

	private emitCompany(): void {
		this.selectedCompanyEvents.emit(this.company);
	}

	displayCompanyName(company: Company): string {
		return company?.name ?? '';
	}

	displayCompanySearchItem(item: any): string {
		return item?.name ?? item;
	}

	onCompanySelected(company: Company): void {
		if (company.id !== undefined) {
			this.company = company;
			this.emitCompany();
		}
	}

	onCancelClicked(): void {
		this.dialogRef.close(false);
	}

	onNextClicked(): void {
		this.showTransferDeviceOwnershipViewDisabled = false;
	}

	async onTransferDeviceOwnershipClicked(): Promise<void> {
		// make sure the user can't transfer the box multiple times (creating duplicate DeviceAssignment records in the database) from clicking the
		// button multiple times quickly
		if (!this.serverCallInProgress) {
			this.serverCallInProgress = true;
			const deviceIds = this.selectedDevices.map((device) => {
				return device.id;
			});

			await this.apiService
				.transferDeviceOwnership(deviceIds, this.company.id)
				.then((_) => {
					this.showTransferDeviceOwnershipConfirmation = true;
					this.showTransferDeviceOwnershipConfirmationSuccess = true;
				})
				.catch((_) => {
					this.showTransferDeviceOwnershipConfirmation = true;
					this.showTransferDeviceOwnershipConfirmationSuccess = false;
				});

			this.serverCallInProgress = false;
		}
	}

	onRetryClicked(): void {
		this.showTransferDeviceOwnershipConfirmation = false;
	}

	onConfirmClicked(): void {
		this.dialogRef.close(true);
	}

	getApiService() {
		return this.apiService;
	}
}
